import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Button from '../button';
import CommonCard from '../card/CommonCard';
import { Lock, Unlock, Tournament, Ranking } from '../../assets/images';
import { ContentsIconDefault } from '../../assets/images/contentsIconDefault';
import { formatDateTimeDisplay } from '../../utils/base';
import { CHAMPIONSHIP_TYPES } from '../../constants';

const ChampionshipCard = ({
    empty = false,
    type = 0, // 0: 'ranking', 1: 'tournament'
    status = 0, // 0 : 등록, 1: 삭제
    contentName = '마운틴클라이머',
    name = '2학년 3반 토너먼트 모여라!',
    locked = true,
    startDate = '',
    endDate = '',
    currentWinner = '',
    icon = null,
    onButtonClick = null,
    buttonText = '',
    showDeleteButton = false,
    onDelete = null,
    onClick = null,
    completed, // Extract to prevent passing to DOM
    isMine,
    playerCount,
    contentIndex,
    id,
    sx = {},
    ...props
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    // Color schemes based on type and state (completed: state === 2)
    const getColorScheme = () => {
        if (empty === true) {
            return {
                backgroundColor: theme.palette.figma.block,
                borderColor: null,
                nameColor: theme.palette.figma.buttonText,
                winnerColor: theme.palette.figma.yellowText,
            };
        }
        const inProgress = completed !== true;
        if (type === 1) {
            if (inProgress) {
                return {
                    backgroundColor: theme.palette.figma.champTournament,
                    borderColor: theme.palette.figma.champTournamentHighlight,
                    nameColor: theme.palette.figma.yellowText,
                    winnerColor: theme.palette.figma.yellowText,
                };
            } else {
                return {
                    backgroundColor: theme.palette.figma.champTournamentFinish,
                    borderColor: null,
                    nameColor: theme.palette.figma.buttonText,
                    winnerColor: theme.palette.figma.yellowText,
                };
            }
        }

        if (type === 0) {
            if (inProgress) {
                return {
                    backgroundColor: theme.palette.figma.champRanking,
                    borderColor: theme.palette.figma.champRankingHighlight,
                    nameColor: theme.palette.figma.yellowText,
                    winnerColor: theme.palette.figma.didim,
                };
            } else {
                return {
                    backgroundColor: theme.palette.figma.champRankingFinish,
                    borderColor: null,
                    nameColor: theme.palette.figma.buttonText,
                    winnerColor: theme.palette.figma.yellowText,
                };
            }
        }
        return {};
    };

    const colorScheme = getColorScheme();

    const containerStyles = {
        position: 'relative',
        display: 'flex',
        width: '340px',
        padding: empty === true && buttonText.length === 0 ? '30px 20px' : '20px',
        flexDirection: 'column',
        gap: '10px',
        alignSelf: 'stretch',
        borderRadius: '20px',
        backgroundColor: colorScheme.backgroundColor,
        ...(colorScheme.borderColor && {
            border: `1px solid ${colorScheme.borderColor}`,
        }),
        ...sx,
    };

    const headerStyles = {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        alignSelf: 'stretch',
    };

    const iconStyles = {
        width: '70px',
        height: '70px',
        borderRadius: '72px',
        border: '2px solid white',
        overflow: 'hidden',
        flexShrink: 0,
        position: 'relative',
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
    };

    const contentStyles = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '5px',
        flex: '1 0 0',
    };

    const contentNameStyles = {
        fontSize: '18px',
        fontWeight: 600,
        color: theme.palette.figma.buttonText,
        lineeight: 'normal',
        letterSpacing: '-0.54px',
        whiteSpace: 'nowrap',
        textAlign: 'left',
    };

    const typeBadgeStyles = {
        display: 'flex',
        gap: '5px',
        alignItems: 'center',
    };

    const badgeStyles = {
        display: 'flex',
        padding: '5px 10px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '5px',
        position: 'relative',
        zIndex: 1,
        minWidth: type === 1 ? '86px' : '66px',
        maxWidth: '120px',
        overflow: 'hidden',
    };

    const badgeBackgroundStyles = {
        width: '100%',
        height: '30px',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '10px',
        opacity: 0.2,
        background: theme.palette.figma.black,
        zIndex: 0,
    };

    const badgeTextStyles = {
        fontSize: '12px',
        fontWeight: 500,
        letterSpacing: '-0.36px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flex: '1 1 0',
        minWidth: 0,
        lineHeight: 'normal',
        position: 'relative',
        zIndex: 2,
    };

    // info 영역 높이 계산 (state 기준: completed)
    const getInfoHeight = () => {
        if (type === 1 && completed !== true) {
            return '39px';
        } else if (type === 1 && completed === true) {
            return '59px';
        } else {
            return '76px';
        }
    };

    const infoStyles = {
        display: 'flex',
        padding: '10px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'stretch',
        borderRadius: '15px',
        position: 'relative',
        zIndex: 1,
        minHeight: getInfoHeight(),
    };

    const infoBackgroundStyles = {
        width: '300px',
        height: getInfoHeight(),
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '15px',
        opacity: 0.4,
        background: theme.palette.figma.black,
        zIndex: 0,
    };

    const nameStyles = {
        fontSize: '16px',
        fontWeight: 600,
        color: colorScheme.nameColor,
        letterSpacing: '-0.48px',
        alignSelf: 'stretch',
        position: 'relative',
        zIndex: 2,
    };

    const dateStyles = {
        fontSize: '12px',
        fontWeight: 400,
        color: theme.palette.figma.iconWhite,
        letterSpacing: '-0.36px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        alignSelf: 'stretch',
        position: 'relative',
        zIndex: 2,
        opacity: 0.6
    };

    const winnerStyles = {
        fontSize: '14px',
        fontWeight: 600,
        color: colorScheme.winnerColor,
        letterSpacing: '-0.42px',
        textAlign: 'center',
        alignSelf: 'stretch',
        position: 'relative',
        zIndex: 2,
    };

    const emptyTextStyles = {
        textAlign: 'center',
        fontFamily: 'Pretendard',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        letterSpacing: '-0.42px',
    };

    const getTypeIcon = () => {
        if (type === 1) {
            return (
                <Tournament />
            );
        } else if (type === 0) {
            return (
                <Ranking />
            );
        } else {
            return (null);
        }
    };

    const getTypeText = () => {
        const item = CHAMPIONSHIP_TYPES.find((item) => item.value === type);
        return item ? t(item.label) : '';
    };

    const getTypeTextColor = () => {
        if (type === 1) return '#81D9FF';
        if (type === 0) return '#F9A0FF';
        return theme.palette.figma.buttonText;
    };

    if (empty === true) {
        return (
            <CommonCard sx={containerStyles} {...props}>
                <Typography sx={emptyTextStyles}>
                    {status === 'ongoing'
                        ? t('no_my_championship')
                        : t('no_ongoing_championship')}
                </Typography>
                {onButtonClick && (
                    <Button onClick={onButtonClick}>
                        {buttonText}
                    </Button>
                )}
            </CommonCard>
        );
    }

    return (
        <CommonCard sx={containerStyles} onClick={onClick} {...props}>
            <Box sx={headerStyles}>
                {icon ? (
                    <Box sx={iconStyles}>
                        <img src={icon} alt={contentName} />
                    </Box>
                ) : <ContentsIconDefault />}
                <Box sx={contentStyles}>
                    <Typography sx={contentNameStyles}>{contentName}</Typography>
                    <Box sx={typeBadgeStyles}>
                        <Box sx={{ position: 'relative', zIndex: 2 }}>
                            {locked ? <Lock /> : <Unlock />}
                        </Box>
                        <Box sx={badgeStyles}>
                            <Box sx={badgeBackgroundStyles} />
                            <Box sx={{ position: 'relative', zIndex: 2 }}>
                                {getTypeIcon()}
                            </Box>
                            <Typography
                                sx={{
                                    ...badgeTextStyles,
                                    color: getTypeTextColor(),
                                }}
                            >
                                {getTypeText()}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {showDeleteButton && (
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onDelete) onDelete();
                        }}
                        sx={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            minWidth: 'auto',
                            width: '24px',
                            height: '24px',
                            padding: 0,
                            backgroundColor: 'transparent',
                            color: theme.palette.figma.white[60],
                            fontSize: '16px',
                            fontWeight: 400,
                            borderRadius: '50%',
                            zIndex: 10,
                            '&:hover': {
                                backgroundColor: theme.palette.figma.white[20],
                                color: theme.palette.figma.Red_didim,
                            }
                        }}
                    >
                        ×
                    </Button>
                )}
            </Box>
            <Box sx={infoStyles}>
                <Box sx={infoBackgroundStyles} />
                <Typography sx={nameStyles}>{name}</Typography>
                {type === 0 && (
                    <Typography sx={dateStyles}>{formatDateTimeDisplay(startDate, 'date')} ~ {formatDateTimeDisplay(endDate, 'date')}</Typography>
                )}
                {(type === 0 || completed === true) && currentWinner.length > 0 && (
                    <Typography sx={winnerStyles}>{completed === true ? t('winner') : t('current_1st')} : {currentWinner}</Typography>
                )}
            </Box>
        </CommonCard >
    );
};

export default ChampionshipCard;
